import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card, IconSvg, Main_Plain } from "monica-alexandria";
import { Sales_Lead_Avatar } from "./Sales_Lead_Avatar";
import {
    read_lead_assignee,
    read_lead_assignee_member,
    read_lead_age_label,
    read_lead_name,
    read_lead_stalling_schedule_label,
    read_lead_stalling_schedule_status,
    read_lead_source_labels,
    read_member_keys,
    read_member_name
} from "../utils/sales_helpers";

const ProspectLeadCard = styled(Card)`
    position: relative;
    padding: var(--normalPads);
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    gap: 0.8rem;
    height: auto;
    min-height: auto;
    overflow: visible;
    background: linear-gradient(180deg, ${p => p.theme.background} 0%, ${p => p.theme.low} 100%);
    cursor: pointer;

    &[data-dragging="true"] {
        opacity: 0.55;
    }
`;

const ProspectLeadDragHandle = styled.button`
    position: absolute;
    top: 0.8rem;
    right: 0.8rem;
    z-index: 3;
    width: 3.2rem;
    height: 3.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    outline: none;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    box-shadow: ${p => p.theme.out};
    cursor: ${p => p.disabled ? "not-allowed" : "grab"};
    opacity: ${p => p.disabled ? "0.45" : "1"};

    svg {
        width: 1.8rem;
        fill: ${p => p.theme.color};
    }

    &:active {
        cursor: grabbing;
    }
`;

const ProspectLeadCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1rem;
`;

const ProspectLeadCardTitle = styled.div`
    display: grid;
    gap: 0.3rem;
`;

const ProspectLeadTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const ProspectLeadFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    h6 {
        color: ${p => p.theme.high};
    }
`;

const ProspectLeadAssignee = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;

    h6 {
        color: ${p => p.theme.high};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 12rem;
    }
`;

const ProspectLeadTag = styled.div`
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    background: ${p => p.theme.mid};

    h5 {
        white-space: nowrap;
    }
`;

const ProspectLeadSchedule = styled.div`
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    border: 0.1rem solid ${p => p.theme.mid};

    &.success {
        border-color: var(--success);
    }

    &.warning {
        border-color: var(--warning);
    }

    &.error {
        border-color: var(--error);
    }

    h6 {
        color: ${p => p.theme.color};
    }

    &.success h6 {
        color: var(--success);
    }

    &.warning h6 {
        color: var(--warning);
    }

    &.error h6 {
        color: var(--error);
    }
`;

const ProspectLeadUnassigned = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--smallPads);
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    border: 0.1rem solid var(--warning);

    h6 {
        color: var(--warning);
    }
`;

/**
 * Reads the same stable card key used by the Sales board drag state.
 */
const read_sales_card_key = (lead) => {
    return `${lead?.brandId || lead?._id || lead?.id || ""}`.trim();
};

/**
 * Renders one draggable prospect lead card inside the board.
 */
export const Sales_Lead_Card = (props) => {
    const { t } = useTranslation();
    const leadName = read_lead_name(props?.lead) || t("Unnamed lead");
    const brandName = props?.lead?.brand || leadName;
    const leadAssignee = read_lead_assignee(props?.lead);
    const assigneeMember = read_lead_assignee_member(props?.lead, props?.teamMembers);
    const assigneeName = read_member_name(assigneeMember || leadAssignee) || t("Unassigned");
    const leadUnassigned = read_member_keys(leadAssignee).length === 0;
    const sourceLabels = read_lead_source_labels(props?.lead, props?.teamMembers);
    const stallingScheduleLabel = read_lead_stalling_schedule_label(props?.lead);
    const stallingScheduleStatus = read_lead_stalling_schedule_status(props?.lead);
    const showDragHandle = Boolean(props?.dragAllowed && !props?.dragDisabled);
    const cardKey = read_sales_card_key(props?.lead);

    return (
        <ProspectLeadCard
            data-dragging={props?.dragLeadId === cardKey ? "true" : "false"}
            onClick={() => props?.onOpenLead(props?.lead)}
        >
            {showDragHandle ? (
                <ProspectLeadDragHandle
                    type="button"
                    draggable={true}
                    disabled={false}
                    title={t("Drag")}
                    onClick={(event) => event.stopPropagation()}
                    onDragStart={(event) => props?.onDragStart(event, cardKey)}
                    onDragEnd={props?.onDragEnd}
                >
                    <IconSvg Icon="Ham" />
                </ProspectLeadDragHandle>
            ) : null}
            <ProspectLeadCardHeader>
                <ProspectLeadCardTitle>
                    <h4>{brandName}</h4>
                    {brandName !== leadName ? <h5>{leadName}</h5> : null}
                </ProspectLeadCardTitle>
            </ProspectLeadCardHeader>

            <ProspectLeadTags>
                {sourceLabels.map((sourceLabel, sourceIndex) => (
                    <ProspectLeadTag key={`${sourceLabel}-${sourceIndex}`}>
                        <h5>{sourceLabel}</h5>
                    </ProspectLeadTag>
                ))}
            </ProspectLeadTags>
            {stallingScheduleLabel ? (
                <ProspectLeadSchedule className={stallingScheduleStatus}>
                    <h6>{stallingScheduleLabel}</h6>
                </ProspectLeadSchedule>
            ) : null}
            <ProspectLeadFooter>
                <h6>{read_lead_age_label(props?.lead)}</h6>
                <ProspectLeadAssignee>
                    <Sales_Lead_Avatar
                        member={assigneeMember}
                        label={assigneeName}
                        name={assigneeName}
                        size="2.4rem"
                    />
                </ProspectLeadAssignee>
            </ProspectLeadFooter>
            {leadUnassigned ? (
                <ProspectLeadUnassigned>
                    <h6>{t("Unassigned")}</h6>
                    {props?.canClaim ? (
                        <Main_Plain
                            size="small"
                            text={props?.claiming ? t("Assigning") : t("Assign to me")}
                            disabled={props?.claiming}
                            onClick={(event) => {
                                event.stopPropagation();
                                props?.onClaim(props?.lead);
                            }}
                        ></Main_Plain>
                    ) : null}
                </ProspectLeadUnassigned>
            ) : null}
        </ProspectLeadCard>
    );
};
